import { useCallback, useEffect, useRef, useState } from 'react';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { FILES_BASE_PATH } from '../utils/pathsConstants';
import { handleError } from '../utils/utils';

import { MediaType } from '../types/enums/mediatype.enum';

import { Media } from '../types/mediaTypes';
import { MediaUsages } from '../types/usageTypes';

export enum SortByOption {
    Type = 'type',
    Url = 'url',
    Created = 'created',
    Usages = 'usages',
}
export enum Order {
    ASC = 'asc',
    DESC = 'desc',
}

export type ListMediasParams = {
    page?: number;
    limit?: number;
    sortBy?: SortByOption;
    order?: Order;
    filterByType?: MediaType[];
    filterByDateFrom?: string;
    filterByDateTo?: string;
};

type MediasResponse = {
    medias: Media[];
    usagesByMediaId: Record<Media['id'], MediaUsages>;
    pagination: {
        page: number;
        limit: number;
        totalPageCount: number;
    };
};

export function useMedias(listMediasParams?: ListMediasParams) {
    async function listMedias(params?: ListMediasParams) {
        try {
            const { data } = await axios.get<MediasResponse>('/api/media/v2', {
                params,
            });
            return data;
        } catch (err: unknown) {
            handleError('List medias error', error, false);
            throw err;
        }
    }

    const { isPending, error, data, refetch } = useQuery({
        queryKey: ['medias', listMediasParams],
        queryFn: () => listMedias(listMediasParams),
    });

    return {
        mediasResponse: data,
        refetch,
        isPending,
        error,
    };
}

export function useMedia(
    id?: number,
    options: {
        refetchInterval?: number;
    } = {},
) {
    async function getMedia(id: number) {
        const { data } = await axios.get<Media>(`/api/media/v2/${id}`);
        return data;
    }

    const { isPending, error, data, refetch } = useQuery({
        queryKey: ['media', id],
        queryFn: id ? () => getMedia(id) : skipToken,
        ...options,
    });

    return {
        mediasResponse: data,
        refetch,
        isPending,
        error,
    };
}

export function useDeleteMedia() {
    const [isDeleted, setIsDeleted] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();
    const queryClient = useQueryClient();

    async function deleteMedia(id: Media['id']) {
        setIsDeleted(false);
        setIsLoading(true);
        setError(undefined);
        try {
            await axios.delete(`/api/${FILES_BASE_PATH}/media/${id}`);
            queryClient.invalidateQueries({ queryKey: ['medias'] });
            setIsDeleted(true);
        } catch (err: unknown) {
            setError(handleError('Delete media error', err, false));
        }
        setIsLoading(false);
    }

    return {
        deleteMedia,
        isLoading,
        isDeleted,
        error,
    };
}

export function useMediaProcessingStatus(handlers: {
    onProcessingComplete: (media: Media) => void;
    onProcessingError: () => void;
}) {
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [media, setMedia] = useState<Media>();
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    const polledMedia = useMedia(media?.id, {
        refetchInterval: isPolling ? 1000 : undefined,
    });

    const trackMediaProcessing = useCallback((media: Media) => {
        setMedia(media);
        setIsPolling(true);
    }, []);

    const processingStatus =
        polledMedia.mediasResponse?.processingStatus ??
        media?.processingStatus ??
        'completed';
    const processingProgress =
        polledMedia.mediasResponse?.processingProgress ??
        media?.processingProgress ??
        0;

    useEffect(() => {
        if (
            polledMedia.mediasResponse &&
            (processingStatus === 'completed' || processingStatus === 'error')
        ) {
            setIsPolling(false);
            if (processingStatus === 'completed') {
                handlersRef.current.onProcessingComplete(
                    polledMedia.mediasResponse,
                );
            } else if (processingStatus === 'error') {
                handlersRef.current.onProcessingError();
            }
        }
    }, [processingStatus, polledMedia.mediasResponse]);

    const status = {
        processingState: processingStatus,
        processingProgress,
    };

    return {
        processingStatus: media ? status : undefined,
        trackMediaProcessing,
        media,
    };
}

/**
 * Processing results reported by DraggablePhotos, kept apart from the entity's
 * media state so a finished conversion doesn't look like an unsaved change.
 */
export function useProcessedMediaStatuses() {
    const [statuses, setStatuses] = useState<
        Record<number, Media['processingStatus']>
    >({});

    const getStatus = (media?: {
        id?: number | string;
        processingStatus?: Media['processingStatus'];
    }) => {
        const reported =
            typeof media?.id === 'number' ? statuses[media.id] : undefined;
        return reported ?? media?.processingStatus;
    };

    const onProcessingComplete = useCallback((media: Media) => {
        setStatuses((current) => ({
            ...current,
            [media.id]: media.processingStatus,
        }));
    }, []);

    const onProcessingError = useCallback((mediaId: number) => {
        setStatuses((current) => ({ ...current, [mediaId]: 'error' }));
    }, []);

    return { getStatus, onProcessingComplete, onProcessingError };
}
